import { AppAudioPlayer } from "../../audio/AppAudioPlayer";
import { OptionsItemLiveData, OptionsItemThemeData, OptionsLiveData } from "../../types/types";
import { JSONDeepClone } from "../../utils/JSONDeepClone";
import { ThemeDataManager } from "./ThemeDataManager";
import { AppLocalStorage } from "./AppLocalStorage";
import { VariationId } from "../screen/TableScreen";
import { AppLocalization } from "../localization/AppLocalization";
import { CardsSorting, GamePlayDirection, GameSpeed } from "./AppSettings";
import { OptionsItemKeys } from "./LayoutDataManager";
import { AppDebug } from "../debug/AppDebug";
import GameScene from "../../scenes/GameScene";
import { OptionsContentScroll } from "../screen/options/OptionsContentScroll";
import { OptionsToggleItem } from "../screen/options/OptionsToggleItem";
import { ScreenManager, GraphicsQuality } from "../../managers/ScreenManager";

import shared_options_default from "../data/shared/shared_options_data.json";
import belote_options_default from "../data/belote/belote_options_data.json";
import coinche_options_default from "../data/coinche/coinche_options_data.json";
import contree_options_default from "../data/contree/contree_options_data.json";
import { CardDecksManager } from "./CardDecksManager";
import { LoaderFileKeys } from "../constant/AppConstants";


export enum LiveDataChangeEvents {
    BACKGROUND_CHANGE = "backgroundChange",
    CARD_DECK_CHANGE = "cardDeckChange",
    CARD_BACK_CHANGE = "cardBackChange",
    MUSIC_TRACK_CHANGE = "musicTrackChange",
    LANGUAGE_CHANGE = "languageChange",
    POINTS_PER_GAME_CHANGE = "pointsPerGameChange",
}

export type ChangedItemData = {
    live: OptionsItemLiveData,
    /**
     * NOTE: EDIT: Only for shared data. For variation item data, this is not required (?)
     */
    theme?: OptionsItemThemeData,
}

/**
 * Live options data manager
 * - options LIVE data is shared among all variations (changing background in one variation changes it in all)
 * - structurally its item data referenced by key: Record<string, OptionsItemLiveData>
 * - actual listing of items in OptionsScreen is done using VariationLayoutData @see LayoutDataManager
 * This class:
 * - retrieve item data by key
 * - cache live data
 * - resolve change between cached and live data and (emit event[s] to) update (external) views
 *   - dispatch CHANGE event 
 */
export class OptionsDataManager {
    public static sharedDataDefaults: OptionsLiveData;
    public static sharedData: OptionsLiveData;
    public static sharedDataCached: OptionsLiveData;

    public static variationDataDefaults: OptionsLiveData[];
    public static variationData: OptionsLiveData[];
    public static variationDataCached: OptionsLiveData[];

    private static eventEmitter: Phaser.Events.EventEmitter;
    private static scene: GameScene;


    public static Init(scene: GameScene): void {
        this.eventEmitter = new Phaser.Events.EventEmitter();
        this.scene = scene;

        // sharedDataDefaults
        this.sharedDataDefaults = shared_options_default;
        // sharedData
        this.sharedData = AppLocalStorage.GetOptionsSharedData(null) ?? JSONDeepClone(this.sharedDataDefaults);
        // sharedDataCached
        this.sharedDataCached = JSONDeepClone(this.sharedData);

        // variationDataDefaults
        this.variationDataDefaults = [
            belote_options_default, 
            coinche_options_default, 
            contree_options_default,
        ];

        this.variationData = [];
        this.variationDataCached = [];
        for (let i=0; i<3; i++) {
            // variationData
            this.variationData[i] = AppLocalStorage.GetOptionsVariationData(i, null) ?? JSONDeepClone(this.variationDataDefaults[i]);
            // variationDataCached
            this.variationDataCached[i] = JSONDeepClone(this.variationData[i]);
        }
    }

    public static GetEventEmitter(): Phaser.Events.EventEmitter {
        return this.eventEmitter;
    }

    public static SaveSharedData(): void {
        AppLocalStorage.SetOptionsSharedData(this.sharedData);
    }

    public static SaveVariationData(variationId: VariationId): void {
        AppLocalStorage.SetOptionsVariationData(variationId, this.variationData[variationId]);
    }

    public static GetDefaultItemDataByItemId(
        itemId: string,
        variationId: VariationId = undefined,
    ): OptionsItemLiveData | undefined {
        shared_options_default
        let sharedItemData: OptionsItemLiveData = this.sharedDataDefaults.itemsById[itemId];
        if (variationId == undefined) {
            return sharedItemData;
        }
        else {
            return this.variationDataDefaults[variationId].itemsById[itemId] ?? sharedItemData;
        }
    }

    /**
     * Return OptionsItemLiveData for this item.
     * NOTE: return variation-specific setting primarily, shared setting secondarily, which allows 
     *       for having both, where variation-specific (takes precedence) OVERRIDES (over) shared.
     * @param itemId 
     * @param variationId 
     * @returns 
     */
    public static GetItemDataByItemId(
        itemId: string,
        variationId: VariationId = undefined,
    ): OptionsItemLiveData | undefined {
        let sharedItemData: OptionsItemLiveData = this.sharedData.itemsById[itemId];
        // if variationId was not supplied
        if (variationId == undefined) {
            // just return what you get from shared data
            return sharedItemData;
        }
        // if variationId was supplied
        else {
            // return either variation item data or shared item data
            // (which ever is not undefined, if any)
            return this.variationData[variationId].itemsById[itemId] ?? sharedItemData;
        }
    }
    
    public static GetQuality(): GraphicsQuality {
        const graphicsQualityIndex = this.sharedData.itemsById[OptionsItemKeys.graphicsQuality].value as number;
        return graphicsQualityIndex;
    }

    /**
     * Translates current indexed "directionOfPlay" into enum GamePlayDirection and returns it
     * @returns 
     */
    public static GetGameplayDirection(): GamePlayDirection {
        const directionOfPlayIndex = this.sharedData.itemsById[OptionsItemKeys.directionOfPlay].value as number;
        switch (directionOfPlayIndex) {
            // "Counter-Clockwise" eg GamePlayDirection=-1
            case 1:
                return GamePlayDirection.CW;
            // "Clockwise" eg GamePlayDirection=1
            case 0:
            default:
                return GamePlayDirection.CCW;
        }
    }

    /**
     * Translates current indexed "gameSpeed" into enum GameSpeed and returns it
     * @returns 
     */
    public static GetGameSpeed(): GameSpeed {
        const gameSpeedIndex = this.sharedData.itemsById[OptionsItemKeys.gameSpeed].value as number;
        switch (gameSpeedIndex) {
            case 0:
                return GameSpeed.SLOW;
            case 1:
                return GameSpeed.NORMAL;
            case 2:
                return GameSpeed.FAST;
            case 3:
                return GameSpeed.VERY_FAST;
            // unknown is "Normal"
            default:
                return GameSpeed.NORMAL;
        }
    }

    /**
     * cardsSorting:
     * "Left-Handed" = 
     * "Right-Handed" = 
     * @returns 
     */
    public static GetCardsSorting(): CardsSorting {
        const cardsSortingIndex = this.sharedData.itemsById[OptionsItemKeys.cardsSorting].value as number;
        switch (cardsSortingIndex) {
            // "Left-Handed" eg CardsSorting=0
            case 0:
                return CardsSorting.LEFT_HANDED;
            // "Right-Handed" eg CardsSorting=1
            case 1:
            default:
                return CardsSorting.RIGHT_HANDED;
        }
    }

    /*
        NOTE: ES doesn't do this, they keep stack flow constant.
        Lefty (visually): A♥ 5♠ 6♠ K♥ K♦
        Righty (visually): K♦ K♥ 6♠ 5♠ A♥
    */
    public static IsRankLeftToRight(): boolean {
        return this.GetCardsSorting() == CardsSorting.LEFT_HANDED;
    }

    public static IsStackLeftToRight(): boolean {
        return this.GetCardsSorting() == CardsSorting.RIGHT_HANDED;
    }

    // public static GetPointsPerGame(variationId: VariationId): number
    public static GetPointsPerGame(): number {
        const pointsPerGame: OptionsItemLiveData = this.sharedData.itemsById[OptionsItemKeys.pointsPerGame];
        const pointsPerGameIndex: number = (pointsPerGame && (pointsPerGame.value != undefined)) ? pointsPerGame.value as number : 0;
        const pointsPerGameValue: number = AppLocalization.GetSharedData().options.itemsById[OptionsItemKeys.pointsPerGame].choices[pointsPerGameIndex].itemLabel as number;
        return pointsPerGameValue;
    }

    /**
     * 
     * @param value if left empty, returns just the gameSpeed multiplier.
     * @returns 
     */
    public static GetTiming(value: number = 1): number {
        if (AppDebug.IsFastestSpeed()) {
            return value / 10;
        }
        return value / this.GetGameSpeed();
    }

    /**
     * Set defaults options (all items), apdate view and state
     * EDIT: Since this is extremely difficult to achieve, we simply set default data as current and reload the webpage (after popup YES confirm).
     * @param variationId 
     * @param saveData now always saves
     */
    public static SetDefaults(
        variationId: VariationId = undefined,
    ): void
    {
        // A) complex approach: on popup CONFIRM choice, 
        //    rebuild whole screen, stop/play approp. music, swap cardDeck etc etc (WIP)
        // set live to defaults (NOTE: only need to change live, cached is refreshed in @see ResolveLiveDataChange)
        // - shared
        // this.sharedData = JSONDeepClone(this.sharedDataDefaults);
        // // - variation-specific
        // if (variationId != undefined) {
        //     this.variationData[variationId] = JSONDeepClone(this.variationDataDefaults[variationId]);
        // }
        // // update options screen
        // // - dirty options screen
        // (this.scene as GameScene).optionsScreen.SetDirty(true);
        // // - rebuild whole screen
        // // (this.scene as GameScene).optionsScreen.SetData(variationId);
        // (this.scene as GameScene).optionsScreen.Hide(false);
        // (this.scene as GameScene).optionsScreen.Show(false);
        // // trigger all changes
        // this.ResolveLiveDataChange(variationId, saveData);

        // 
        // const cardsDeckIndex = this.GetDefaultItemDataByItemId(LoaderFileKeys.cardsDeck).value as number;
        // AppLocalStorage.GetCardsDeckIndex(cardsDeckIndex);
        // AppLocalStorage.SetCardsDeckIndex(cardsDeckIndex);
        // AppLocalStorage.SetMusicTrackIndex

        // B) on popup CONFIRM choice: 
        //    - simply reload page
        AppLocalStorage.SetOptionsSharedData(this.sharedDataDefaults);
        if (variationId != undefined) {
            AppLocalStorage.SetOptionsVariationData(variationId, this.variationDataDefaults[variationId]);
        }
        location.reload();
    }

    public static ResolveLiveDataChange(
        variationId: VariationId = undefined,
        saveData: boolean = false,
    ): void {
        // console.log("ResolveLiveDataChange",VariationId[variationId], saveData);

        // 1) parse changes in sharedData
        let changedDatasById: Map<string, ChangedItemData> = new Map<string, ChangedItemData>();
        for (var itemKey in this.sharedData.itemsById) {
            // console.log(itemKey, this.sharedData.itemsById[itemKey])
            if (this.sharedData.itemsById[itemKey].value != this.sharedDataCached.itemsById[itemKey].value) {
                // console.log(itemKey, this.sharedData.itemsById[itemKey].value, this.sharedDataCached.itemsById[itemKey].value)
                const themeItemData: OptionsItemThemeData = ThemeDataManager.GetSharedData().options.itemsById[itemKey];
                const changedItemData: ChangedItemData = {
                    live: this.sharedData.itemsById[itemKey],
                    theme: themeItemData,
                };
                changedDatasById.set(itemKey, changedItemData);
            }
        }
        // got some changed props from shared data
        if (changedDatasById.size > 0) {
            changedDatasById.forEach((value: ChangedItemData, key: string) => {
                // console.log("> "+key, value);
                this.ResolveOptionsItemDataChange(key, value);
            });

            // cache new live data
            this.sharedDataCached = JSONDeepClone(this.sharedData);

            // save to harddrive?
            if (saveData) {
                this.SaveSharedData();
            }
        }

        // 2) parse changes in variationData
        if (variationId != undefined) {
            changedDatasById.clear();
            // const variationData: OptionsLiveData = this.variationData[variationId];
            for (var prop in this.variationData[variationId].itemsById) {
                // console.log(prop, this.data.itemsById[prop])
                if (this.variationData[variationId].itemsById[prop].value != this.variationDataCached[variationId].itemsById[prop].value) {
                    const changedItemData: ChangedItemData = {
                        live: this.variationData[variationId].itemsById[prop],
                        // theme: themeItemData,
                    };
                    // console.log(`[${liveItemData.id}] new=${liveItemData.value} old=${cachedItemData.value}`);
                    changedDatasById.set(prop, changedItemData);
                }
            }
            // got some changed props from variation data
            if (changedDatasById.size > 0) {
                changedDatasById.forEach((value: ChangedItemData, key: string) => {
                    // console.log(`> ${key} (${value})`);
                    this.ResolveOptionsItemDataChange(key, value);
                });

                // cache new live data
                this.variationDataCached[variationId] = JSONDeepClone(this.variationData[variationId]);

                // save to harddrive?
                if (saveData) {
                    this.SaveVariationData(variationId);
                }
            }
        }
    }

    /**
     * NOTE: 
     * - shared data has both itemData.live and itemData.theme filled in
     * - variation data has only itemData.live filled in
     * @param itemId 
     * @param itemData 
     */
    private static ResolveOptionsItemDataChange(itemId: string, itemData: ChangedItemData): void {
        const itemValue: number | boolean = itemData.live.value;
        switch (itemId) {
            case OptionsItemKeys.applicationLanguage:
                this.eventEmitter.emit(
                    LiveDataChangeEvents.LANGUAGE_CHANGE,
                    itemData
                );
                break;

            case OptionsItemKeys.graphicsQuality:
                // trigger onResize updates contents scales
                ScreenManager.SetScreenScale(itemValue as GraphicsQuality, true, true);
                break;

            case OptionsItemKeys.cardDeck:
                // change the actual data model, before saving it to HDD
                // react to cards deck index changte by changing views (by changing DT contents)
                // AppLocalStorage.SetCardsDeckIndex(cardDeckIndex);
                const cardsDeckIndex: number = itemData.live.value as number;
                const cardDeck: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.cardDeck);
                cardDeck.value = cardsDeckIndex;

                this.eventEmitter.emit(
                    LiveDataChangeEvents.CARD_DECK_CHANGE,
                    cardsDeckIndex,
                );
                break;

            case OptionsItemKeys.cardBack:
                // change the actual data model, before saving it to HDD
                const cardBackIndex: number = itemData.live.value as number;
                const cardBack: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.cardBack);
                cardBack.value = cardBackIndex;

                this.eventEmitter.emit(
                    LiveDataChangeEvents.CARD_BACK_CHANGE,
                    itemData
                );
                // AppLocalStorage.SetCardsBackIndex(itemValue as number);
                break;

            case OptionsItemKeys.background:
                // console.log("load background index:", itemValue);
                this.eventEmitter.emit(
                    LiveDataChangeEvents.BACKGROUND_CHANGE,
                    itemData
                );
                // EDIT: we now take bgIndex from live data only.
                // AppLocalStorage.SetTableBackgroundIndex(itemValue as number);
                break;

            case OptionsItemKeys.musicTrack:
                // console.log("TODO: load background index:", itemValue);

                // AppLocalStorage.SetMusicTrackIndex(itemValue as number);
                const musicTrackIndex = itemValue as number;
                const musicTrack: OptionsItemLiveData = OptionsDataManager.GetItemDataByItemId(OptionsItemKeys.musicTrack);
                musicTrack.value = musicTrackIndex;
                
                AppAudioPlayer.PlayMusicTrackByIndex(musicTrackIndex);
                break;

            case OptionsItemKeys.pointsPerGame:
                this.eventEmitter.emit(
                    LiveDataChangeEvents.POINTS_PER_GAME_CHANGE,
                    itemData.live.value,
                    false, // mark OptionsScreen as Dirty
                );
                break;

            case OptionsItemKeys.enableGameTips:
                // if enableGameTips was just turned off
                if (!itemData.live.value) {
                    const autoShowGameTips: OptionsToggleItem = (this.scene.optionsScreen.contentsScrollPane as OptionsContentScroll).GetItemById(OptionsItemKeys.autoShowGameTips) as OptionsToggleItem;
                    // if autoShowGameTips is on
                    if (autoShowGameTips.IsSelected()) {
                        // turn autoShowGameTips off
                        autoShowGameTips.Click();
                    }
                }
                break;

            case OptionsItemKeys.autoShowGameTips:
                // if autoShowGameTips was just turned on
                if (itemData.live.value) {
                    const enableGameTips: OptionsToggleItem = (this.scene.optionsScreen.contentsScrollPane as OptionsContentScroll).GetItemById(OptionsItemKeys.enableGameTips) as OptionsToggleItem;
                    // if enableGameTips is off
                    if (!enableGameTips.IsSelected()) {
                        // turn enableGameTips on
                        enableGameTips.Click();
                    }
                }
                break;

            // default:
            //     console.warn("Unhandled data change: ", OptionsItemKeys[itemId], itemValue);
        }
    }

    public static HasItemDataByItemId(
        variationId: VariationId,
        itemId: string,
    ): boolean {
        return this.variationData[variationId].itemsById[itemId] != undefined;
    }

    public static IsPissTrump(variationId: VariationId): boolean {
        const pissTrump: OptionsItemLiveData = this.GetItemDataByItemId(
            OptionsItemKeys.pissTrump,
            variationId
        );
        return (pissTrump && (pissTrump.value as boolean));
    }
}